import { PlusCircleIcon } from '@mm/icons';
import classNames from 'classnames';
import React from 'react';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../../common/components/GreenMarker/GreenMarker';
import { PrimaryToolTip } from '../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { trackWebAppGAEvent } from '../../../../../../common/helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import { campaignTypeByKind, UIFilterCampaignKindType } from '../types';
import { useCreateCampaignHandlers } from '../use-create-campaign-handlers';
import './CreateCampaignButton.pcss';

type Props = {
  className?: string;
  kind?: UIFilterCampaignKindType;
  withPlusIcon?: boolean;
  prepend?: string;
  disabled?: boolean;
  showGreenMarker?: boolean;
};

const CreateCampaignButtonView = ({
  className,
  kind,
  withPlusIcon,
  prepend,
  disabled,
  showGreenMarker,
}: Props) => {
  const { createByKind, validationErrorsMap } = useCreateCampaignHandlers();

  if (kind === 'legacy_messenger_ads') {
    return null;
  }

  const validationError = kind ? validationErrorsMap[kind] : false;

  const onClick = () => {
    trackWebAppGAEvent('click', {
      category: 'create_campaign',
      label: `create_button_${kind}`,
    });
    createByKind(kind);
  };

  const { name } = kind ? campaignTypeByKind[kind] : { name: 'Campaign' };

  const isDisabled = !!validationError || disabled;

  const button = (
    <div className="create-campaign-button__container">
      {showGreenMarker && (
        <GreenMarker className="create-campaign-button__container-green-marker" />
      )}
      <PrimaryButton
        disabled={isDisabled}
        className={classNames(className)}
        leftIcon={withPlusIcon && <PlusCircleIcon />}
        onClick={onClick}
      >
        {prepend && `${prepend} `}
        {name}
      </PrimaryButton>
    </div>
  );

  if (disabled) return button;

  if (validationError) {
    const title = typeof validationError === 'string' ? validationError : '';
    return (
      <PrimaryToolTip
        title={title}
        anchorElement={button}
        classNameContent={classNames(className)}
        useHover
      />
    );
  }

  return button;
};

export const CreateCampaignButton = (props: Props) => {
  const { isChatBlasterActivated, isDripCampaignsActivated } =
    useGreenCueMarkers();

  const { kind } = props;

  // Note: We introduce inter-dependency between the cues here.
  // Drips are more important than regular blasts, so we want to show Drips first
  const showChatBlasterGreenMarker =
    kind === 'chat_blaster' &&
    !isChatBlasterActivated &&
    isDripCampaignsActivated;
  const showDripCampaignGuardGreenMarker =
    kind === 'drip_campaigns' && !isDripCampaignsActivated;
  const showGreenMarker =
    showChatBlasterGreenMarker || showDripCampaignGuardGreenMarker;

  const { isSmsSupported } = usePhoneNumber();
  const guardIf = !(
    kind &&
    ['chat_blaster', 'drip_campaigns'].includes(kind) &&
    isSmsSupported
  );

  return (
    <TooltipGuard guardIf={guardIf}>
      {isAllowed => (
        <CreateCampaignButtonView
          {...props}
          disabled={!isAllowed}
          showGreenMarker={showGreenMarker}
        />
      )}
    </TooltipGuard>
  );
};
